import {
  useState, useCallback, useEffect, useRef, ReactElement, HTMLProps,
  KeyboardEvent, MouseEvent, Fragment, CSSProperties
} from "react"
import {Fzf, extendedMatch} from "fzf"
import "./TextWithSuggestions.scss"

function createIntervalsFromSet(set: Set<number>): ReadonlyArray<[number, number]> {
  const inOrder = [...set].sort((a, b) => a - b)
  return inOrder.reduce((prev, newItem) => {
    if (prev.length === 0) {
      return [[newItem, newItem + 1]]
    }
    const lastItem = prev[prev.length - 1]
    if (lastItem[1] === newItem) {
      return [...prev.slice(0, -1), [lastItem[0], newItem + 1]]
    }
    return [...prev, [newItem, newItem + 1]]
  }, new Array<[number, number]>())
}

type TriggerChar = "#" | "@"
type Props = Omit<HTMLProps<HTMLTextAreaElement>, "value" | "onChange"> & {
  triggers: Partial<{[key in TriggerChar]: ReadonlyArray<[string, string]>}>
  value: string,
  setValue: (newValue: string) => void,
}

type CurrentTrigger = {
  triggerChar: TriggerChar,
  search: string,
  startIndex: number
  endIndex: number
  lineNumber: number
}

export default function TextAreaWithSuggestions(
  {triggers, className, value, setValue, ...props}: Props
): ReactElement {
  const thisElement = useRef<HTMLDivElement>(null)
  const [currentTrigger, setCurrentTrigger] = useState<CurrentTrigger | null>(null)
  const [forceCursorPos, setForceCursorPos] = useState<number | null>(null)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)

  useEffect(() => {
    if (!thisElement.current) {
      return
    }
    if (forceCursorPos === null) {
      return
    }
    const ta = thisElement.current.querySelector("textarea")!
    ta.selectionStart = forceCursorPos
    ta.selectionEnd = forceCursorPos
    setForceCursorPos(null)
  }, [forceCursorPos])

  useEffect(() => {
    if (!thisElement.current) {
      return
    }
    const selectedItem = thisElement.current.querySelector(".item.selected")
    if (selectedItem) {
      selectedItem.scrollIntoView({block: "nearest"})
    }
  }, [selectedIndex])

  function replaceTriggerText(replacement: string) {
    if (!thisElement.current) {
      throw new Error("Element not referred")
    }
    if (!currentTrigger) {
      throw new Error("No current trigger to replace")
    }
    const newValue = value.slice(0, currentTrigger.startIndex)
      + replacement
      + value.slice(currentTrigger.endIndex)
    const newpos = currentTrigger.startIndex + replacement.length
    setValue(newValue)
    setForceCursorPos(newpos)
  }

  function cursorChangeEvent<
    E extends KeyboardEvent<HTMLTextAreaElement>
    | MouseEvent<HTMLTextAreaElement>
  >(e: E, chainedHandler: ((e: E) => void) | undefined) {
    if (chainedHandler) {
      chainedHandler(e)
    }
    if ("code" in e && e.code === "Escape") {
      // after escape we don't want the list to reappear
      return
    }
    calculateAndSetCurrentTrigger()
  }

  const calculateAndSetCurrentTrigger = useCallback(() => {
    const definedTriggers = Object.keys(triggers).join("")
    const TRIGGER_RE = new RegExp(
      `(?<triggerChar>[${definedTriggers}])(?<search>[^${definedTriggers}\\n]*)$`)

    if (!thisElement.current) {
      return
    }
    const ta = thisElement.current.querySelector("textarea")!
    if (ta.selectionStart === undefined || ta.selectionStart !== ta.selectionEnd) {
      return
    }
    const pos = ta.selectionStart
    const triggerMatch = TRIGGER_RE.exec(value.slice(0, pos))
    if (!triggerMatch) {
      setCurrentTrigger(null)
      return
    }
    const {triggerChar, search} = triggerMatch.groups as {
      triggerChar: TriggerChar, search: string}
    const endIndex = pos
    const startIndex = pos - search.length - triggerChar.length
    const lineNumber = value.slice(0, pos).split("\n").length
    setCurrentTrigger(currentTrigger => {
      if (currentTrigger
        && currentTrigger.triggerChar === triggerChar
        && currentTrigger.search === search
        && currentTrigger.startIndex === startIndex
        && currentTrigger.endIndex === endIndex
        && currentTrigger.lineNumber === lineNumber
      ) {
        return currentTrigger
      }
      return {triggerChar, search, startIndex, endIndex, lineNumber}
    })
  }, [triggers, value])

  useEffect(() => {
    calculateAndSetCurrentTrigger()
  }, [calculateAndSetCurrentTrigger])


  const optionItems = currentTrigger === null ? null
    : new Fzf(triggers[currentTrigger.triggerChar]!,
      {selector: x => x[1], match: extendedMatch,}).find(
        currentTrigger.search)

  useEffect(() => {
    setSelectedIndex(null)
  }, [currentTrigger])

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (!currentTrigger) {
      return
    }
    switch (e.code) {
      case "ArrowUp":
        setSelectedIndex(
          si => (optionItems === null || optionItems.length === 0) ? null :
          (si === null || si === 0) ? optionItems.length - 1 : si - 1)
        break;
      case "ArrowDown":
        setSelectedIndex(
          si => (optionItems === null || optionItems.length === 0) ? null :
          (si === null || si === optionItems.length - 1) ? 0 : si + 1)
        break;
      case "Tab":
      case "Enter":
        if (selectedIndex !== null && optionItems) {
          replaceTriggerText(optionItems[selectedIndex].item[0])
        } else {
          if (props.onKeyDown) {
            props.onKeyDown(e)
          }
          return
        }
        break;
      case "Escape":
        setCurrentTrigger(null)
        break;
      default:
        if (props.onKeyDown) {
          props.onKeyDown(e)
        }
        return
    }
    e.preventDefault()
  }

  return <div
    className={(className ? className + " " : "") + "TextWithSuggestions"}
    ref={thisElement}>
    <textarea {...props}
      value={value}
      onKeyDown={onKeyDown}
      onKeyUp={e => cursorChangeEvent(e, props.onKeyUp)}
      onClick={e => cursorChangeEvent(e, props.onClick)}
      onChange={e => setValue(e.currentTarget.value)}
    />
    {currentTrigger && optionItems &&
    <div className="options"
      style={{"--trigger-line-number": currentTrigger.lineNumber} as CSSProperties}>
      <ul>
        {optionItems.length === 0 && <span>No results</span>}
        {optionItems.map(({item: [insertValue, showValue], positions}, index) => {
          const intervals = createIntervalsFromSet(positions)
          return <li className={index === selectedIndex ? "selected item" : " item"}
            onMouseEnter={() => setSelectedIndex(index)}
            key={index} onClick={() => replaceTriggerText(insertValue)}>
            {intervals.map((interval, index) => {
              const prevIntervalEnd = index === 0
                ? (interval[0] === 0 ? null : 0)
                : intervals[index - 1][1]
              return <Fragment key={index}>
                {prevIntervalEnd !== null && <span className="nohl">
                  {showValue.slice(prevIntervalEnd, interval[0])}
                </span>}
                <span className="hl">{showValue.slice(...interval)}</span>
              </Fragment>
            })}
            <span className="nohl">
              {showValue.slice(
                intervals.length === 0 ? 0 : intervals[intervals.length - 1][1])}
            </span>
          </li>
        })
        }
      </ul>
    </div>
    }
  </div>

}

